import { useState, useEffect } from "react";
import useAdd from "../../api/useAdd";

/**
 * Custom hook for handling password reset functionality
 * @returns {Object} Password reset methods and state
 */
const usePasswordReset = () => {
  const [otpSent, setOtpSent] = useState(false);

  // Hook for requesting password reset OTP
  const {
    handleAdd: requestReset,
    loading: requestLoading,
    error: requestError,
    isSuccess: requestSuccess,
    responseData: requestResponseData,
    SnackbarMessage: RequestSnackbar,
  } = useAdd(
    "password-reset",
    null,
    "Password reset OTP has been sent to your email.",
    "User with this email does not exist.",
  );

  // Hook for verifying OTP and setting new password
  const {
    handleAdd: verifyOTP,
    loading: verifyLoading,
    error: verifyError,
    isSuccess: verifySuccess,
    SnackbarMessage: VerifySnackbar,
  } = useAdd(
    "verify-otp",
    null,
    "Password has been reset successfully. You can now login with your new password.",
    "Failed to verify OTP or reset password",
  );

  /**
   * Request a password reset by sending an OTP to the user's email
   * @param {string} email - User's email address
   */
  const requestPasswordReset = (email) => {
    requestReset({ email });
    // Don't set otpSent immediately - wait for success response
  };

  // Observe requestSuccess and set otpSent when successful
  useEffect(() => {
    if (requestSuccess) {
      setOtpSent(true);
    }
  }, [requestSuccess]);

  /**
   * Verify the OTP and set a new password
   * @param {Object} data - Object containing email, otp, and new_password
   */
  const verifyOTPAndResetPassword = (data) => {
    verifyOTP({
      email: data.email,
      otp: data.otp,
      new_password: data.newPassword,
    });
  };

  // Additional method to handle OTP resend
  const resendOTP = async (email) => {
    try {
      await requestReset({ email });
      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    requestPasswordReset,
    verifyOTPAndResetPassword,
    resendOTP,
    loading: requestLoading || verifyLoading,
    requestSuccess,
    requestResponseData,
    requestError,
    verifySuccess,
    verifyError,
    otpSent,
    RequestSnackbar,
    VerifySnackbar,
  };
};

export default usePasswordReset;
